import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { validateUserAuthentication, createSecureLogger, getUserRole } from '../utils/securityUtils';

interface SecurityGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  redirectTo?: string;
  allowedRoles?: string[];
}

/**
 * SecurityGuard component - provides authentication and authorization protection
 * Wrap any page/component with this to ensure secure access
 */
const SecurityGuard: React.FC<SecurityGuardProps> = ({
  children,
  requireAuth = true,
  redirectTo = '/auth',
  allowedRoles = []
}) => {
  const { user, loading, isAdmin = false, isAdminLoading = false, adminPermissions = [] } = useAuth();
  const navigate = useNavigate();
  const logger = createSecureLogger();
  const requiresAdminRole = allowedRoles.includes('admin');
  // A role-based admin (is_admin=false but granted sections) may enter /admin.
  const hasAdminAccess = isAdmin || adminPermissions.length > 0;

  useEffect(() => {

    // Don't check while auth is still loading
    if (loading || (requiresAdminRole && isAdminLoading)) return;

    // Check authentication requirement
    if (requireAuth && !validateUserAuthentication(user)) {
      logger.warn('Unauthorized access attempt - redirecting to auth', {
        path: window.location.pathname,
        timestamp: new Date().toISOString()
      });
      navigate(redirectTo);
      return;
    }

    // Check role-based authorization if roles are specified
    if (requiresAdminRole && user) {
      if (!hasAdminAccess) {
        logger.warn('Insufficient permissions - access denied', { 
          userId: user.id,
          userRole: 'user',
          requiredRoles: allowedRoles,
          path: window.location.pathname
        });
        navigate('/dashboard'); // Redirect to safe page
        return;
      }
    }

    if (allowedRoles.length > 0 && user && !requiresAdminRole) {
      const userRole = getUserRole(user);
      const hasAllowedRole = allowedRoles.includes(userRole);
      
      if (!hasAllowedRole) {
        logger.warn('Insufficient permissions - access denied', { 
          userId: user.id,
          userRole,
          requiredRoles: allowedRoles,
          path: window.location.pathname
        });
        navigate('/dashboard'); // Redirect to safe page
        return;
      }
    }

  }, [user, loading, isAdmin, isAdminLoading, hasAdminAccess, requiresAdminRole, navigate, requireAuth, redirectTo, allowedRoles, logger]);

  // Show loading while auth is being checked
  if (loading || (requiresAdminRole && isAdminLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Don't render if auth is required but user is not authenticated
  if (requireAuth && !validateUserAuthentication(user)) {
    return null;
  }

  if (requiresAdminRole && user && !hasAdminAccess && !isAdminLoading) {
    return null;
  }

  // Render children if all checks pass
  return <>{children}</>;
};

export default SecurityGuard;
